import {
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    Transition
} from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid'
import { Fragment } from 'react'

type Props = {
    value: ArwenListBoxItem
    options: ArwenListBoxItem[]
    onSelect: (optionName: ArwenListBoxItem) => void
    // TailwindCSS width classes
    widthClass: string
}

export type ArwenListBoxItem = {
    id: string
    name: string
    icon?: string
    /** Renders the option greyed and unselectable. */
    disabled?: boolean
    /** Why the option is disabled, shown on hover. Ignored unless `disabled`. */
    disabledReason?: string
}

export default function ArwenListBox(props: Props) {
    const { value, options, onSelect, widthClass } = props

    const selectedOptionDisplay = (
        <>
            {value.icon && <i className={`fab ${value.icon} w-6 mr-2`}></i>}
            <span>{value.name}</span>
        </>
    )
    return (
        <Listbox
            value={value}
            onChange={(event) => {
                onSelect(event)
            }}
        >
            <div className={`${widthClass}`}>
                <ListboxButton className="relative flex h-9 w-full min-w-36 items-center rounded-md border border-ink-200 bg-white pl-3 pr-9 text-sm text-ink-700 shadow-sm hover:cursor-pointer hover:border-ink-300 hover:bg-ink-50 focus:border-arwen-500 focus:outline-none focus:ring-1 focus:ring-arwen-500">
                    <span className="block truncate">{selectedOptionDisplay || 'Select...'}</span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-4 w-4 text-ink-400" aria-hidden="true" />
                    </span>
                </ListboxButton>
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <ListboxOptions
                        anchor="bottom start"
                        className={`${widthClass} z-50 mt-1 max-h-60 overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
                    >
                        {options.map((option, index) => (
                            <ListboxOption
                                key={index}
                                disabled={option.disabled}
                                data-tooltip-id={option.disabled ? 'tooltip-id' : undefined}
                                data-tooltip-content={
                                    option.disabled ? option.disabledReason : undefined
                                }
                                className={({ active }) => {
                                    // Quiet ink highlight rather than the legacy solid-blue
                                    // active row, matching the rest of the refreshed controls.
                                    // A disabled option stays listed — hiding it would leave
                                    // no way to explain why it can't be chosen.
                                    if (option.disabled) {
                                        return ' cursor-not-allowed select-none text-sm py-1.5 pr-4 list-none text-ink-300'
                                    }
                                    return ` cursor-default select-none text-sm py-1.5 pr-4 list-none ${
                                        active ? 'bg-ink-50 text-ink-900' : 'text-ink-700'
                                    }`
                                }}
                                value={option}
                            >
                                {({ selected }) => (
                                    <>
                                        <span className="flex flex-row">
                                            {selected ? (
                                                <span className="flex items-center pl-3 text-arwen-600">
                                                    <CheckIcon
                                                        className="h-5 w-5"
                                                        aria-hidden="true"
                                                    />
                                                </span>
                                            ) : null}
                                            <span
                                                className={`inset-y-0 left-0 block truncate ${
                                                    selected ? 'pl-2' : 'font-normal pl-10'
                                                }`}
                                            >
                                                {option.icon && (
                                                    <i className={`fab ${option.icon} w-6 `}></i>
                                                )}
                                                {option.name}
                                            </span>
                                        </span>
                                    </>
                                )}
                            </ListboxOption>
                        ))}
                    </ListboxOptions>
                </Transition>
            </div>
        </Listbox>
    )
}
